import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal, untracked } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';

import { DataTableComponent, TableCellDirective, TableColumn } from '@libs/front/components/data-table/data-table.component';
import { ButtonListComponent } from '@libs/front/components/button-list/button-list.component';
import { Delivery, JsonDelivery } from 'src/app/bo/delivery';
import { DeliveryModaleComponent } from 'src/app/components/deliveries/delivery-modale/delivery-modale.component';

import { BlastHolesStateService } from '../../blast-holes-state.service';
import { readColumnKeys, storeColumnKeys } from '../../shared/column-preferences';

/** One table row; keys match the column keys. Dates are epoch ms so the table sorts them. */
interface DeliveryRow {
  id:         string;
  name:       string;
  /** Start / end as epoch ms (site local rendering by the date pipe); NaN when missing */
  start:      number;
  end:        number;
  /** Blaster, first line of the Users cell */
  blaster:    string;
  /** Operators, second line of the Users cell */
  operators:  string;
  /** Searchable / sortable text of the Users column */
  users:      string;
  holeCount:  number;
  kg:         number;
  detonators: number;
  cartridges: number;
  boosters:   number;
  details:    string;
  /** Details opens the legacy edit modal: simple deliveries only (legacy rule) */
  canEdit:    boolean;
}

const COLUMNS: readonly TableColumn[] = [
  { key: 'start',      label: 'HOLES_TAB.DELIVERY_COL_START' },
  { key: 'end',        label: 'HOLES_TAB.DELIVERY_COL_END' },
  { key: 'name',       label: 'HOLES_TAB.DELIVERY_COL_NAME' },
  { key: 'users',      label: 'HOLES_TAB.DELIVERY_COL_USERS' },
  { key: 'holeCount',  label: 'HOLES_TAB.DELIVERY_COL_HOLES' },
  { key: 'kg',         label: 'HOLES_TAB.DELIVERY_COL_KG' },
  { key: 'detonators', label: 'HOLES_TAB.DELIVERY_COL_DETONATORS' },
  { key: 'cartridges', label: 'HOLES_TAB.DELIVERY_COL_CARTRIDGES' },
  { key: 'boosters',   label: 'HOLES_TAB.DELIVERY_COL_BOOSTERS' },
  { key: 'details',    label: 'HOLES_TAB.DELIVERY_COL_DETAILS', sortable: false },
];

/** Columns shown by default (Figma); the picker can add name, detonators, cartridges and boosters. */
const DEFAULT_COLUMN_KEYS = ['start', 'end', 'users', 'holeCount', 'kg', 'details'];

/** Marker colour of the holes loaded by the selected delivery. */
const DELIVERY_HOLE_COLOR = '#6C1D45';

/**
 * Holes › Delivery view (no legend).
 * Delivery table ported from the legacy `deliveries` component onto the kit data-table:
 * start, end, users (blaster / operators), holes count, kg sent, Details. Selecting a delivery
 * highlights its holes on the map (from the delivery log fetched on demand). "Details" opens the
 * legacy delivery modal (ngx-bootstrap) for simple deliveries; the API enforces the EditDelivery
 * policy on save, as in the legacy tab.
 * Design source : Figma 2544:671025
 */
@Component({
  selector:        'app-holes-delivery-view',
  standalone:      true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    DecimalPipe,
    TranslateModule,
    DataTableComponent,
    TableCellDirective,
    ButtonListComponent,
  ],
  templateUrl: './holes-delivery-view.component.html',
  styleUrls:   ['./holes-delivery-view.component.scss'],
})
export class HolesDeliveryViewComponent {
  protected readonly state = inject(BlastHolesStateService);
  private readonly translate  = inject(TranslateService);
  private readonly modals     = inject(BsModalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route      = inject(ActivatedRoute);
  private readonly router     = inject(Router);

  /** Delivery to select on arrival, from `?deliveryId=` */
  private readonly requestedDeliveryId = toSignal(
    this.route.queryParamMap.pipe(map(params => params.get('deliveryId'))),
    { initialValue: null as string | null },
  );

  private readonly lang = toSignal(
    this.translate.onLangChange.pipe(map(event => event.lang)),
    { initialValue: this.translate.currentLang },
  );

  /** Set when the log of the selected delivery could not be fetched (no highlight possible). */
  protected readonly logError = signal(false);

  // ── Deliveries ────────────────────────────────────────────────────────────

  /** Deliveries of the blast, most recent first (legacy default order). */
  protected readonly deliveries = computed<Delivery[]>(() =>
    [...(this.state.deliveries() ?? [])].sort((a, b) => toTime(b.startTime) - toTime(a.startTime)),
  );

  private readonly deliveriesById = computed(() => new Map(this.deliveries().map(delivery => [delivery.deliveryId, delivery])));

  /**
   * publicGuids of the holes of the selected delivery, from its log: by hole guid first, then by
   * hole name (older Expertab logs carry the name only). Undefined until the log is loaded.
   */
  private readonly selectedDeliveryHoles = computed<ReadonlySet<string> | undefined>(() => {
    const selected = this.state.selectedDeliveryId();
    const log = selected ? this.state.deliveryLogs().get(selected) : undefined;
    if (!log) return undefined;

    const byGuid = new Map(this.state.holes().map(hole => [hole.publicGuid, hole.publicGuid]));
    const byName = new Map(this.state.holes().map(hole => [hole.name, hole.publicGuid]));
    const result = new Set<string>();
    for (const hole of log.holesDelivery ?? []) {
      const id = (hole.holeGuid && byGuid.get(hole.holeGuid)) || byName.get(hole.holeId);
      if (id) result.add(id);
    }
    return result;
  });

  // ── Table ─────────────────────────────────────────────────────────────────

  protected readonly allColumns = COLUMNS as TableColumn[];
  /** User's column choice, remembered in the browser (see shared/column-preferences.ts) */
  protected readonly visibleColumnKeys = signal<Set<string>>(readColumnKeys('delivery', COLUMNS, DEFAULT_COLUMN_KEYS));
  protected readonly visibleColumns = computed(() =>
    this.allColumns.filter(column => this.visibleColumnKeys().has(column.key)),
  );

  protected onColumnsChange(keys: Set<string>): void {
    this.visibleColumnKeys.set(keys);
    storeColumnKeys('delivery', keys);
  }

  protected readonly rows = computed<DeliveryRow[]>(() => {
    this.lang();
    const details = this.translate.instant('HOLES_TAB.DELIVERY_DETAILS');
    const notSet  = this.translate.instant('HOLES_TAB.DELIVERY_NOT_SET');
    return this.deliveries().map(delivery => {
      const blaster   = delivery.blaster?.trim() || notSet;
      const operators = delivery.operatorsNames?.trim() || notSet;
      return {
        id:         delivery.deliveryId,
        name:       delivery.name ?? '',
        start:      toTime(delivery.startTime),
        end:        toTime(delivery.endTime),
        blaster,
        operators,
        users:      `${blaster} / ${operators}`,
        holeCount:  delivery.holeCount ?? 0,
        kg:         toNumber(delivery.kgDelivered),
        detonators: toNumber(delivery.detonators),
        cartridges: toNumber(delivery.cartridges),
        boosters:   toNumber(delivery.boosters),
        details,
        canEdit:    !!delivery.isSimpleDelivery,
      };
    });
  });

  constructor() {
    // Deliveries of the blast
    effect(() => {
      if (this.state.blastId()) untracked(() => void this.state.loadDeliveries());
    });

    // `?deliveryId=` (set by the site Planning tab): select that delivery once the blast's list has
    // arrived, as a row click would, then drop the param so it is not re-applied later.
    effect(() => {
      const requested = this.requestedDeliveryId();
      const deliveries = this.state.deliveries();
      if (!requested || !deliveries) return;
      untracked(() => {
        if (deliveries.some(delivery => delivery.deliveryId === requested)) {
          this.state.selectedDeliveryId.set(requested);
        }
        void this.router.navigate([], {
          relativeTo:          this.route,
          queryParams:         { deliveryId: null },
          queryParamsHandling: 'merge',
          replaceUrl:          true,
        });
      });
    });

    // Fetch the log of the selected delivery (once per guid) to know its holes
    effect(() => {
      const selected = this.state.selectedDeliveryId();
      untracked(() => {
        this.logError.set(false);
        if (!selected) return;
        void this.state.loadDeliveryLog(selected).then(log => {
          if (!log && this.state.selectedDeliveryId() === selected) this.logError.set(true);
        });
      });
    });

    // Highlight the holes of the selected delivery on the map; restore the status icons otherwise
    effect(() => {
      const inDelivery = this.selectedDeliveryHoles();
      if (!inDelivery) {
        this.state.holeColors.set(null);
        return;
      }
      const colors = new Map<string, string | null>();
      for (const hole of this.state.holes()) {
        colors.set(hole.publicGuid, inDelivery.has(hole.publicGuid) ? DELIVERY_HOLE_COLOR : null);
      }
      this.state.holeColors.set(colors);
    });
    this.destroyRef.onDestroy(() => this.state.holeColors.set(null));
  }

  // ── Handlers ──────────────────────────────────────────────────────────────

  protected onRowClick(row: DeliveryRow): void {
    this.state.toggleDelivery(row.id);
  }

  protected onDetails(row: DeliveryRow, event: Event): void {
    event.stopPropagation();
    const delivery = this.deliveriesById().get(row.id);
    if (!delivery || !row.canEdit) return;
    void this.state.loadDeliveryLog(row.id).then(log => {
      if (log) this.openDeliveryModal(delivery, log);
    });
  }

  protected retry(): void {
    void this.state.loadDeliveries(true);
  }

  protected isMissing(time: number): boolean {
    return Number.isNaN(time);
  }

  // ── Internals ─────────────────────────────────────────────────────────────

  /**
   * Opens the legacy "Modify load" modal (declared in app.module, opened through BsModalService like
   * the ImportModal of the container). The list is re-fetched when the modal closes with a change.
   */
  private openDeliveryModal(delivery: Delivery, jsonDelivery: JsonDelivery): void {
    const modalRef = this.modals.show(DeliveryModaleComponent, {
      backdrop:            true,
      ignoreBackdropClick: false,
      keyboard:            false,
      class:               'modal-lg',
      animated:            false,
      initialState:        { jsonDelivery, delivery } as Partial<DeliveryModaleComponent>,
    });
    const subscription = modalRef.content?.onClose.subscribe((changed: boolean) => {
      subscription?.unsubscribe();
      if (changed) void this.state.loadDeliveries(true);
    });
  }
}

/** Epoch ms of an ISO date string; NaN when missing or unparsable. */
function toTime(value: string | Date | null | undefined): number {
  return value ? new Date(value).getTime() : NaN;
}

/** The legacy DTO types some quantities as strings; the table sorts numbers. */
function toNumber(value: string | number | null | undefined): number {
  const parsed = typeof value === 'number' ? value : parseFloat(value ?? '');
  return Number.isNaN(parsed) ? 0 : parsed;
}
